import { teileAntwort } from "../../shared/antwort-stimme.js";
import { istEigeneSeite } from "./app-browser.js";
import { ohneAudioMarke } from "../../shared/audio-marke.js";
import { inlineAufzaehlung } from "./aufzaehlung.js";
import { adressenImText, linkZiel } from "../../shared/link-erkennung.js";
/**
 * voice-v8 — Schlanker Markdown→DOM-Renderer für Bot-Bubbles.
 *
 * Unterstützt:
 *   **bold**, *italic*, `code`, [text](url)
 *   einfache Bullet-Listen (- foo / * foo / • foo), auch mitten im Block
 *   Numbered Listen (1. foo)
 *   Statusköpfe „Fertig:“ / „Im Bau:“ / „Braucht dich:“ am Zeilenanfang (fett)
 *   Headings (#/##/### → h2/h3/h4, nur einzeilige Blöcke)
 *   Tabellen (| a | b | + Separatorzeile) — Pre.786, Ticket #52
 *   Doppel-Newline = Absatz
 *   Single-Newline = <br>
 *
 * Bewusst KEIN innerHTML — alles via createElement, keine XSS-Vektoren.
 * Bewusst KEINE md-Bibliothek (marked & Co. liefern HTML-Strings und
 * bräuchten einen Sanitizer — bricht die createElement-Garantie).
 * Kein Codeblock-Support (```), bisher nicht gebraucht.
 */

/**
 * ⚠️ 2026-08-26: Fett und Kursiv duerfen einen Zeilenumbruch enthalten.
 *
 * Vorher sperrte `[^*\n]` jeden Umbruch — und damit blieb auf der Seite
 * "Was ist neu?" rohes Markdown sichtbar stehen, sobald eine Hervorhebung im
 * Fliesstext umbrach: `**nie gefragt, ob eine Seite\nkomponiert ist**` wurde
 * nicht erkannt und Wort fuer Wort mit Sternchen ausgegeben.
 *
 * Das ist gefahrlos, weil der Text weiter unten (Z. ~397) VOR diesem Ausdruck
 * an Leerzeilen in Bloecke zerlegt wird: ein `**` kann keinen Absatz
 * verschlucken, weil es nie ueber eine Leerzeile hinwegkommt.
 *
 * Code-Spannen und Links behalten ihre Zeilensperre — sie brechen im
 * Fliesstext nicht um, und ein `\n` darin waere eher ein Tippfehler.
 */
const INLINE_RE =
  /(\*\*[^*]+\*\*|\*[^*]+\*|`[^`\n]+`|\[[^\]\n]+\]\([^)\n]+\))/g;

/*
 * 26.09.2026 (Nachbesserung zu pre.3352, Ticket 2679): Fett und Kursiv
 * setzten ihren Inhalt als reinen Text — ein Link oder eine Nummer darin
 * blieb toter Text. Jetzt läuft der Inhalt durch denselben Weg wie jeder
 * andere Text; nur ein Zeilenumbruch darin bleibt, wie er war (kein <br>,
 * siehe oben: Fett darf im Fließtext umbrechen).
 */
function renderInline(parent: HTMLElement, text: string, umbruchAlsBr = true): void {
  const parts = text.split(INLINE_RE);
  for (const part of parts) {
    if (!part) continue;
    if (part.startsWith("**") && part.endsWith("**") && part.length >= 4) {
      const b = document.createElement("strong");
      renderInline(b, part.slice(2, -2), false);
      parent.appendChild(b);
    } else if (part.startsWith("*") && part.endsWith("*") && part.length >= 3) {
      const i = document.createElement("em");
      renderInline(i, part.slice(1, -1), false);
      parent.appendChild(i);
    } else if (part.startsWith("`") && part.endsWith("`") && part.length >= 3) {
      // 2026-08-07 (Ticket 1612): Kurzschrift wie `ssh root@…`, eine IP oder
      // ein Pfad ist am Handy sonst nur mit Markieren zu greifen — mit dicken
      // Fingern auf einer Zeile Monospace eine Zumutung. Ein Tipp kopiert.
      parent.appendChild(kopierbarerCode(part.slice(1, -1)));
    } else if (
      part.startsWith("[") &&
      part.includes("](") &&
      part.endsWith(")")
    ) {
      const closeBracket = part.indexOf("](");
      const label = part.slice(1, closeBracket);
      const url = part.slice(closeBracket + 2, -1);
      // Ticket 2679: `[Billing](github.com/…)` und „HTTPS://…“ — dieselbe
      // Regel wie im Fließtext (shared/link-erkennung.ts).
      const ziel = url.startsWith("/") || url.startsWith("mailto:") ? url : linkZiel(url);
      if (ziel && isSafeUrl(ziel)) {
        const a = document.createElement("a");
        a.href = ziel;
        neuesFensterAusser(a);
        a.textContent = label;
        parent.appendChild(a);
      } else {
        parent.appendChild(document.createTextNode(part));
      }
    } else if (!umbruchAlsBr) {
      appendMitLinks(parent, part);
    } else {
      // Plain text — innerhalb noch Single-Newlines zu <br> machen.
      const lines = part.split("\n");
      lines.forEach((line, idx) => {
        if (idx > 0) parent.appendChild(document.createElement("br"));
        if (line) appendMitLinks(parent, line);
      });
    }
  }
}

// Pre.786 (#52): Markdown-Tabellen. Eine Tabellen-Zeile ist |…|…|, die
// ZWEITE Zeile eines Runs muss die Separatorzeile sein (|---|:--:|…).
// Ausrichtung seit Ticket 2683: siehe spaltenKlassen().
const PIPE_ROW_RE = /^\s*\|.*\|\s*$/;
const PIPE_SEP_RE = /^\s*\|(\s*:?-+:?\s*\|)+\s*$/;

function splitCells(line: string): string[] {
  return line
    .trim()
    .replace(/^\|/, "")
    .replace(/\|$/, "")
    .split("|")
    .map((c) => c.trim());
}

/*
 * Ticket 2683 (29.09.2026, Martin: „die rechte Spalte ist nicht rechtsbündig,
 * sie wirkt dadurch ein bisschen verloren"): Seit die Tabelle die Blase füllt
 * (width: 100 %), verteilt der Browser die übrige Breite auf ALLE Spalten im
 * Verhältnis ihres Inhalts. Eine Spalte aus einzelnen Wörtern („mittel") bekam
 * so am Desktop rund 140 px Leerraum hinter ihrem Text — ganz rechts sah das
 * verrutscht aus, Beträge standen links statt Stelle unter Stelle.
 *
 * Jetzt nimmt genau EINE Spalte die übrige Breite auf: die mit dem längsten
 * Text. Spalten, die ohnehin nicht umbrechen (ein Wort je Zelle) oder Zahlen
 * sind, bleiben so schmal wie ihr Inhalt (md-knapp) — am Telefon ändert das
 * an Umbrüchen nichts, ein einzelnes Wort bricht dort auch jetzt nicht.
 * Zahlen- und Betragsspalten stehen rechtsbündig. Ein Ausrichtungszeichen
 * der Separatorzeile (|---:| rechts, |:---:| Mitte, |:---| links) gilt vor
 * der Erkennung: schreibt das Modell es hin, entscheidet es selbst.
 */
/** Zahl oder Betrag: „1.240,00 €", „−12 %", „EUR 90", „3,5 km", „2675" —
 *  aber kein Datum („03.10.") und keine Kennung („2026-041"). */
const ZAHL_RE =
  /^[+\-−±]?\s?(?:[€$£¥]|[A-Z]{3})?\s?(?:\d{1,3}(?:[.,'’\u00A0\u202F ]\d{3})+|\d+)(?:[.,]\d+)?\s?(?:%|‰|[€$£¥]|[A-Za-zµ]{1,3}\.?)?$/u;

/** Zelltext ohne Hervorhebung und Linkziel — nur für die Einordnung. */
function zellText(zelle: string): string {
  return zelle.replace(/\[([^\]]*)\]\([^)]*\)/g, "$1").replace(/[*`]/g, "").trim();
}

/** Längste Zelle (Zeichen), bis zu der eine Spalte ohne Leerzeichen „knapp“ (ohne Umbruch) bleibt. */
const KNAPP_MAX = 24;

function spaltenKlassen(kopf: string[], trenner: string[], zeilen: string[][]): string[] {
  const spalten = kopf.map((_, i) =>
    // Leer, „—" oder „-" ist ein Platzhalter und zählt für keine Seite.
    zeilen.map((z) => zellText(z[i] ?? "")).filter((t) => /[\p{L}\p{N}]/u.test(t)),
  );
  const istZahl = spalten.map((werte) => {
    const zahlen = werte.filter((t) => ZAHL_RE.test(t)).length;
    return zahlen > 0 && zahlen * 2 > werte.length;
  });
  // Die Spalte, die die übrige Breite aufnimmt: der längste Text, keine Zahl.
  let fuellt = 0;
  let laengste = -1;
  spalten.forEach((werte, i) => {
    const laenge = Math.max(0, ...werte.map((t) => t.length));
    if (!istZahl[i] && laenge > laengste) {
      laengste = laenge;
      fuellt = i;
    }
  });
  return spalten.map((werte, i) => {
    const zahl = istZahl[i] ?? false;
    // Ticket 3475: ein langes „Wort“ (Link, Pfad) macht die Spalte nicht knapp — sie dürfte sonst nie umbrechen und sprengte die Blase.
    const knapp = i !== fuellt && (zahl || werte.every((t) => !/\s/.test(t) && t.length <= KNAPP_MAX));
    return [zahl ? "md-zahl" : "", ausrichtung(trenner[i] ?? "", zahl), knapp ? "md-knapp" : ""]
      .filter(Boolean)
      .join(" ");
  });
}

/** Das Zeichen der Separatorzeile gilt vor der Erkennung. */
function ausrichtung(zeichen: string, zahl: boolean): string {
  if (/^:-+:$/.test(zeichen)) return "md-mitte";
  if (/^-+:$/.test(zeichen)) return "md-rechts";
  if (/^:-+$/.test(zeichen)) return "";
  return zahl ? "md-rechts" : "";
}

/** Ein Link, Pfad oder sehr langes Wort in der Zelle (Linkziel zählt mit) — nur dort darf mitten im Wort gebrochen werden. */
function hatLangesWort(zelle: string): boolean {
  return zelle.split(/\s+/).some((w) => w.length > KNAPP_MAX);
}

/**
 * Ticket 3475: rollt die Tabelle wirklich (breiter als der Rahmen), ist der Rahmen per Tastatur erreichbar und benannt;
 * sonst bekommt er weder Fokus noch Rolle (kein Tabulator-Halt ohne Nutzen).
 */
function zeigeRollRahmen(wrap: HTMLElement): void {
  if (typeof ResizeObserver === "undefined") return;
  const pruefe = (): void => {
    const rollt = wrap.scrollWidth > wrap.clientWidth + 1;
    if (rollt === wrap.hasAttribute("tabindex")) return;
    if (rollt) {
      wrap.tabIndex = 0;
      wrap.setAttribute("role", "region");
      wrap.setAttribute("aria-label", "Tabelle, seitlich scrollbar");
    } else {
      wrap.removeAttribute("tabindex");
      wrap.removeAttribute("role");
      wrap.removeAttribute("aria-label");
    }
  };
  const beobachter = new ResizeObserver(pruefe);
  beobachter.observe(wrap);
  const tabelle = wrap.firstElementChild;
  if (tabelle) beobachter.observe(tabelle);
}

function renderTableRun(target: HTMLElement, run: string[]): void {
  const wrap = document.createElement("div");
  wrap.className = "md-table-wrap";
  const table = document.createElement("table");
  table.className = "md-table";
  const kopf = splitCells(run[0] ?? "");
  const zeilen = run.slice(2).map(splitCells);
  const klassen = spaltenKlassen(kopf, splitCells(run[1] ?? ""), zeilen);
  const thead = document.createElement("thead");
  const headRow = document.createElement("tr");
  kopf.forEach((cell, i) => {
    const th = document.createElement("th");
    if (klassen[i]) th.className = klassen[i];
    renderInline(th, cell);
    headRow.appendChild(th);
  });
  thead.appendChild(headRow);
  table.appendChild(thead);
  const tbody = document.createElement("tbody");
  for (const zeile of zeilen) {
    const tr = document.createElement("tr");
    zeile.forEach((cell, i) => {
      const td = document.createElement("td");
      td.className = [klassen[i], hatLangesWort(cell) ? "md-lang" : ""].filter(Boolean).join(" ");
      if (!td.className) td.removeAttribute("class");
      renderInline(td, cell);
      tr.appendChild(td);
    });
    tbody.appendChild(tr);
  }
  table.appendChild(tbody);
  wrap.appendChild(table);
  zeigeRollRahmen(wrap);
  target.appendChild(wrap);
}

function isSafeUrl(url: string): boolean {
  // Browser entfernen Tabulator/Zeilenumbruch und führende Steuerzeichen/
  // Leerraum und lesen "\" wie "/" — dieselbe Sicht prüfen, sonst rutscht
  // "/\t/evil.com" oder "/\\evil.com" als "//evil.com" durch.
  // eslint-disable-next-line no-control-regex
  const u = url.replace(/[\u0000-\u0020\u007f-\u009f\u200b-\u200f\u2028\u2029\ufeff]/g, "").replace(/\\/g, "/");
  if (u.startsWith("//")) return false; // protokollrelativ = fremder Host
  return (
    u.startsWith("http://") ||
    u.startsWith("https://") ||
    u.startsWith("/") ||
    u.startsWith("mailto:")
  );
}

/**
 * Rendert Markdown-Text in das übergebene Element. Element wird vorher
 * geleert. Alle Children sind echte DOM-Knoten (kein innerHTML).
 */
/**
 * 2026-07-25 (Martin: "wenn Links geschickt werden, immer anklickbar machen"):
 * Nackte Adressen im Klartext verlinken. Markdown-Links wurden schon immer
 * umgewandelt; eine einfach hingeschriebene Adresse blieb dagegen toter Text.
 *
 * Bewusst konservativ: nur http/https, und uebliche Satzzeichen am Ende
 * gehoeren NICHT zur Adresse (sonst fuehrt ein Punkt am Satzende ins Leere).
 * Eine schliessende Klammer bleibt nur dann Teil der Adresse, wenn sie auch
 * geoeffnet wurde — sonst zerreisst es Adressen in Klammern.
 */
/*
 * 26.09.2026 (Ticket 2679): auch nackte Domains wie „github.com/settings/…“.
 * Die Erkennung steht seit der Nachbesserung zu pre.3352 in
 * shared/link-erkennung.ts — dieselbe Regel für die Stimme, die eine Adresse
 * nicht vorliest. Dort steht, warum nur Kleinbuchstaben und nur mit Pfad
 * oder „www.“ („Spanisch.Es macht“ wurde ein Link).
 */

/**
 * Telefonnummern antippbar machen.
 *
 * 2026-08-24 (Martin, nach einer Arztsuche: "Ich möchte, dass du mir die Nummer
 * angibst, damit ich nur draufklicken muss zum Anrufen." — und nachdem das
 * System stattdessen einen Kontakt suchte, den es nicht gab: "Warum kann er
 * nicht die Scheiß-Nummer einfach anzeigen?")
 *
 *   Die Nummer stand da: +43 2645 2230, gefunden, vorgelesen — und als toter
 *   Text. Um sie zu nutzen, musste man sie abschreiben.
 *
 * WARUM DAS MUSTER SO ENG IST
 *
 *   Eine zu weite Erkennung ist hier schlimmer als keine. Im selben Text stehen
 *   Dinge, die einer Telefonnummer ähneln und keine sind:
 *
 *     07:30 -11:00, 13:00 -16:00   Öffnungszeiten (genau im Arzt-Text!)
 *     2026-08-24                   ein Datum
 *     8.0.0-pre.2304               eine Version
 *     AT23 3411 4000 0195 5483     eine IBAN
 *
 *   Deshalb: Eine Nummer beginnt mit + und Ländervorwahl ODER mit einer 0 —
 *   und Doppelpunkte, Buchstaben und mehr als vier Gruppen schliessen sie aus.
 *   Ein Doppelpunkt allein wirft jede Uhrzeit heraus.
 */
// Bis zu drei Trennzeichen zwischen den Ziffern: "02622 / 22 773" schreibt man
// in Oesterreich so, mit Leerzeichen um den Schraegstrich.
const TELEFON =
  /(?:\+[1-9]\d{0,2}[ /-]{0,3}|\b0)(?:\d[ /-]{0,3}){6,14}\d\b/g;

/** Ist der Treffer wirklich eine Nummer — oder Uhrzeit, Datum, IBAN? */
function istTelefonnummer(kandidat: string, zeile: string, start: number): boolean {
  // Ziffernzahl: 7 (0664 12345 wäre zu kurz für sich, aber Ortsnetze existieren)
  // bis 15 (E.164-Obergrenze).
  const ziffern = kandidat.replace(/\D/g, "");
  if (ziffern.length < 7 || ziffern.length > 15) return false;
  const davor = zeile.slice(0, start);
  const danach = zeile.slice(start + kandidat.length);

  // Uhrzeit: Der Doppelpunkt steht ZWISCHEN Ziffern ("8:00", "07:30 -11:00").
  // Ein Doppelpunkt nach einem Wort ist dagegen harmlos — "Tel: 038847-335344"
  // ist die haeufigste Schreibweise ueberhaupt und wurde davon zuerst
  // verworfen.
  if (/:/.test(kandidat)) return false;
  if (/\d\s*:\s*$/.test(davor) || /^\s*:\s*\d/.test(danach)) return false;

  // Direkt an Buchstaben angrenzend → Teil einer Kennung (Version, ID).
  if (/[A-Za-zÄÖÜäöü]$/.test(davor) || /^[A-Za-zÄÖÜäöü]/.test(danach)) return false;

  // Punkt mit Ziffer → Version ("8.0.0-pre.2304").
  if (/\d\.$/.test(davor) || /^\.\d/.test(danach)) return false;

  // Mitten aus einer laengeren Zahlenkette geschnitten? Dann ist es keine
  // Nummer, sondern ein Stueck davon — so wurde aus der IBAN
  // "AT23 3411 4000 0195 5483" das Fragment "0195 5483".
  if (/\d[ /-]$/.test(davor)) return false;
  if (/^[ /-]?\d/.test(danach)) return false;

  // Und ausdruecklich: Wo IBAN oder BIC im Satz steht, wird nicht angerufen.
  if (/\b(IBAN|BIC|Kontonummer)\b/i.test(zeile)) return false;
  return true;
}

/** Für den tel:-Verweis: alles ausser Ziffern und führendem + weg. */
function telZiel(kandidat: string): string {
  const plus = kandidat.trimStart().startsWith("+");
  return (plus ? "+" : "") + kandidat.replace(/\D/g, "");
}

/**
 * Chat-Links öffnen in einem neuen Fenster — AUSSER auf eine eigene Seite (29.10.2026, Martin: „voice.vespera.at/neun“ im Chat
 * führte in der App ins Safari-„Not found“). Die App-WebView gibt ein `target="_blank"` ans System weiter, dort fehlt die
 * Anmeldung. Eigene Seiten (gleiche Herkunft, keine Datei) laden darum normal in demselben Fenster; Dateien behalten
 * `_blank`, die App zeigt sie in ihrer Ansicht (lib/app-browser.ts). Fremde Hosts bleiben, wie sie waren.
 */
function neuesFensterAusser(a: HTMLAnchorElement): void {
  if (istEigeneSeite(a.getAttribute("href") ?? "")) return;
  a.target = "_blank";
  a.rel = "noopener noreferrer";
}

function appendMitLinks(parent: HTMLElement, line: string): void {
  let zuletzt = 0;
  for (const t of adressenImText(line)) {
    /*
     * Nachbesserung zu pre.3352: Der Text VOR einer Adresse lief als reiner
     * Text hinein — „Ruf 0664 1234567 an oder schau auf ssi.at/kontakt“ gab
     * nur den Link, die Nummer blieb tot. Jetzt sieht jedes Stück zwischen
     * den Adressen nach Nummern.
     */
    if (t.start > zuletzt) appendMitTelefon(parent, line.slice(zuletzt, t.start));
    if (isSafeUrl(t.ziel)) {
      const a = document.createElement("a");
      a.href = t.ziel;
      neuesFensterAusser(a);
      // Lange Adressen kuerzen — der Verweis bleibt vollstaendig.
      a.textContent = t.text.length > 60 ? t.text.slice(0, 57) + "\u2026" : t.text;
      parent.appendChild(a);
    } else {
      parent.appendChild(document.createTextNode(t.text));
    }
    zuletzt = t.start + t.text.length;
  }
  // Was nach den Adressen bleibt (samt Satzzeichen), noch auf Nummern durchsehen.
  if (zuletzt < line.length) {
    appendMitTelefon(parent, line.slice(zuletzt));
  }
}

/**
 * Telefonnummern im restlichen Text antippbar machen.
 *
 * Getrennt von den Adressen, weil eine Nummer INNERHALB einer Adresse nichts
 * verlinken darf (eine Portangabe ist keine Rufnummer).
 */
function appendMitTelefon(parent: HTMLElement, rest: string): void {
  let zuletzt = 0;
  for (const t of rest.matchAll(TELEFON)) {
    const start = t.index ?? 0;
    const kandidat = t[0];
    if (!istTelefonnummer(kandidat, rest, start)) continue;
    if (start > zuletzt) {
      parent.appendChild(document.createTextNode(rest.slice(zuletzt, start)));
    }
    const a = document.createElement("a");
    a.href = `tel:${telZiel(kandidat)}`;
    a.className = "tel-link";
    // Der sichtbare Text bleibt, wie er geschrieben stand — Martin erkennt
    // seine Nummer wieder, auch wenn der Verweis normalisiert ist.
    a.textContent = kandidat;
    parent.appendChild(a);
    zuletzt = start + kandidat.length;
  }
  if (zuletzt < rest.length) {
    parent.appendChild(document.createTextNode(rest.slice(zuletzt)));
  }
}

/**
 * CSS-Deklarationen (`min-width: 0`, `flex: 1 1 0%`) sind kein Kopier-Chip.
 * Ticket 1612 gilt fuer ssh, IP, Pfad, Version — nicht fuer Stylesheet-Text.
 * Ein role=button darum liest VoiceOver als „Kopieren: min-width: 0" und
 * kann visuell auf einen Streifen zusammenfallen.
 */
const CSS_PROP =
  /^(align-content|align-items|align-self|all|animation|appearance|background|border|bottom|box-shadow|box-sizing|clear|color|column-gap|columns|content|cursor|display|filter|flex|float|font|gap|grid|height|inset|isolation|justify-content|justify-items|justify-self|left|letter-spacing|line-height|list-style|margin|max-height|max-width|min-height|min-width|mix-blend-mode|object-fit|object-position|opacity|order|outline|overflow|padding|place-content|place-items|pointer-events|position|right|row-gap|text-align|text-decoration|text-overflow|text-transform|top|transform|transition|user-select|vertical-align|visibility|white-space|width|word-break|word-wrap|z-index)(-[a-z]+)*$/;

function istCssDeklaration(text: string): boolean {
  const teile = text
    .trim()
    .split(";")
    .map((s) => s.trim())
    .filter(Boolean);
  if (teile.length === 0) return false;
  return teile.every((teil) => {
    const i = teil.indexOf(":");
    if (i <= 0) return false;
    const prop = teil.slice(0, i).trim().toLowerCase();
    const wert = teil.slice(i + 1).trim();
    return wert.length > 0 && CSS_PROP.test(prop);
  });
}

/**
 * Ein `code`-Stueck, das ein Tipp in die Zwischenablage legt
 * (2026-08-07, Ticket 1612).
 *
 * Martin arbeitet vom Handy aus. Ein SSH-Befehl oder eine IP-Adresse per
 * Hand zu markieren ist dort muehsam genug, dass die Angabe faktisch
 * unbrauchbar wird — man tippt sie ab.
 *
 * Bewusst OHNE sichtbaren Knopf: Der wuerde jede Kurzschrift mitten im Satz
 * aufblaehen, und davon gibt es viele. Stattdessen ist das Element selbst
 * die Flaeche, mit `title` als Hinweis und einer kurzen Rueckmeldung, die
 * bestaetigt, dass es geklappt hat — ohne sie tippt man zweimal.
 *
 * CSS-Schnipsel bleiben plain <code> (siehe istCssDeklaration).
 */
function kopierbarerCode(text: string): HTMLElement {
  const c = document.createElement("code");
  c.textContent = text;
  // Stylesheet-Schnipsel bleiben sichtbarer Code, kein Bedienelement.
  if (istCssDeklaration(text)) return c;
  c.className = "md-code-copy";
  c.title = "Tippen zum Kopieren";
  // Fuer Tastatur und Vorlese-Programme ein echter Knopf, nicht nur ein
  // anklickbares Stueck Text.
  c.setAttribute("role", "button");
  c.setAttribute("tabindex", "0");
  c.setAttribute("aria-label", "Kopieren");

  const kopieren = (ev: Event): void => {
    ev.preventDefault();
    ev.stopPropagation();
    const fertig = (): void => {
      c.classList.add("is-copied");
      window.setTimeout(() => c.classList.remove("is-copied"), 1200);
    };
    // navigator.clipboard braucht einen sicheren Kontext; der Rueckfall
    // ueber execCommand haelt es auch dort am Leben, wo es den nicht gibt.
    if (navigator.clipboard?.writeText) {
      void navigator.clipboard.writeText(text).then(fertig, () => {
        if (altModusKopieren(text)) fertig();
      });
    } else if (altModusKopieren(text)) {
      fertig();
    }
  };

  c.addEventListener("click", kopieren);
  c.addEventListener("keydown", (ev) => {
    if (ev.key === "Enter" || ev.key === " ") kopieren(ev);
  });
  return c;
}

/** Rueckfall fuer Umgebungen ohne Clipboard-API. Liefert, ob es klappte. */
export function altModusKopieren(text: string): boolean {
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

export function renderMarkdown(target: HTMLElement, text: string): void {
  while (target.firstChild) target.removeChild(target.firstChild);
  if (!text) return;
  // 2026-06-28 (Martin): interne Audio-Referenz von Sprachnachrichten
  // nicht als rohen Text zeigen. Regel seit 20.09. in shared/audio-marke.ts.
  text = ohneAudioMarke(text);
  /*
   * 2026-08-30: Die Sprechfassung gehoert nicht in die Blase.
   *
   * ⚠️ HIER und nicht bei den Aufrufern. 22 Stellen rendern Nachrichtentext —
   * Chat, Suche, Meldungen, Buch, Alltag. Eine Saeuberung bei den Aufrufern
   * muesste man 22-mal einbauen und beim 23. vergessen; genau dieses Muster
   * hat heute schon viermal zugeschlagen. Die Marke ist eine interne
   * Steueranweisung — sie darf NIRGENDS sichtbar werden, gleich ueber welchen
   * Weg der Text auf den Schirm kommt.
   */
  text = teileAntwort(text).anzeige;
  if (!text) return;

  // 2026-07-07 (Martin: rohe ### und --- in Job-Meldungen): Ueberschriften
  // und Trennstriche auch MITTEN im Block erkennen — vorher galten sie nur
  // als eigenstaendige Bloecke (Doppel-Newline davor/danach noetig).
  text = text
    .replace(/^(#{1,4} .+)$/gm, "\n$1\n")
    .replace(/^\s*-{3,}\s*$/gm, "\n---\n")
    // 20.10.2026 (Martin: „bitte mit Zeilenumbrüchen“): „… erledigt. Im Bau: …“ in EINER Zeile — der Statuskopf beginnt
    // nach einem Satzende eine neue Zeile (renderZeilen setzt ihn fett). Nur diese drei Köpfe, nur nach . ! ?
    // ⚠️ Nie in einer Tabellenzeile (beginnt mit |): ein Umbruch in der Zelle zerbräche die Tabelle.
    .replace(/^.*$/gm, (zeile) =>
      PIPE_ROW_RE.test(zeile) ? zeile : zeile.replace(/([.!?])[ \t]+(Fertig|Im Bau|Braucht dich):(?=\s)/g, "$1\n$2:"),
    );

  // Block-Trennung an Doppel-Newlines.
  const blocks = text.split(/\n{2,}/);
  for (const block of blocks) {
    if (!block.trim()) continue;
    // Rand-Newlines strippen: das \n-Umhuellen der Heading-Zeilen oben
    // laesst am String-Anfang/-Ende ein einzelnes \n am Block kleben
    // (split frisst nur \n{2,}) — der Block wirkte mehrzeilig und eine
    // Ueberschrift in Zeile 1 des Dokuments blieb roh stehen.
    const lines = block.replace(/^\n+|\n+$/g, "").split("\n");

    // Heading? "# ", "## " oder "### " — wir mappen auf h2/h3/h4 weil
    // der Container i.d.R. schon h1 hat. Nur einzeilige Blöcke gelten
    // als Heading (mehrzeilig wäre Absatz mit Hash-Präfix).
    // Trennstrich-Block -> feine Linie.
    if (block.trim() === "---") {
      const hr = document.createElement("hr");
      hr.className = "md-hr";
      target.appendChild(hr);
      continue;
    }
    const headingMatch =
      lines.length === 1 ? /^(#{1,4})\s+(.+)$/.exec(lines[0] ?? "") : null;
    if (headingMatch) {
      const level = headingMatch[1]?.length ?? 1;
      const tag = level === 1 ? "h2" : level === 2 ? "h3" : "h4";
      const h = document.createElement(tag);
      renderInline(h, headingMatch[2] ?? "");
      target.appendChild(h);
    } else {
      renderZeilen(target, lines);
    }
  }
}

/*
 * 20.10.2026 (Martin, 12:32: „mehr Struktur bei Auflistungen, damit das Auge sich leichter tut“): Ein Block wird ZEILENWEISE
 * gelesen, nicht mehr nur als Ganzes. Vorher wurde eine Liste nur erkannt, wenn JEDE Zeile des Blocks ein Punkt war — „Fertig:“
 * mit drei „- …“-Zeilen darunter blieb ein Absatz mit rohen Strichen. Jetzt:
 *   - Zeilen mit „- “, „* “ oder „• “ am Anfang → <ul>, mit „1. “ → <ol> (ab zwei Zeilen, oder der ganze Block — eine einzelne
 *     „20. Oktober …“-Zeile mitten im Absatz bleibt Text). Eine eingerückte Folgezeile gehört zum Punkt davor.
 *   - „Fertig:“, „Im Bau:“, „Braucht dich:“ am Zeilenanfang → eigene Zeile mit fettem Kopf (`.md-abschnitt-kopf`).
 *   - alles andere wie bisher: Absätze (Einzel-Umbruch = <br>), Tabellen, „1) 2) 3)“ im Absatz.
 * ⚠️ Nur DOM-Knoten über renderInline/textContent — kein HTML aus dem Text.
 */
const PUNKT_RE = /^\s*[-*•]\s+/;
const NUMMER_RE = /^\s*(\d+)\.\s+/;
const FOLGEZEILE_RE = /^\s{2,}\S/;
const ABSCHNITT_RE = /^\s*(Fertig|Im Bau|Braucht dich):(?:\s+|$)/;

function renderZeilen(target: HTMLElement, lines: string[]): void {
  let para: string[] = [];
  const flushPara = (): void => {
    if (para.length === 0) return;
    const text = para.join("\n");
    para = [];
    // 24.09.2026: „… — 1) … 2) … 3) …“ in einem Absatz wird eine Liste
    // (frontend/lib/aufzaehlung.ts, Martin: „sehr schwer zu lesen“).
    const auf = inlineAufzaehlung(text);
    if (auf) {
      if (auf.einleitung) {
        const p = document.createElement("p");
        renderInline(p, auf.einleitung);
        target.appendChild(p);
      }
      target.appendChild(liste("ol", auf.punkte));
      return;
    }
    const p = document.createElement("p");
    renderInline(p, text);
    target.appendChild(p);
  };
  const ganzNummeriert = lines.every((l) => NUMMER_RE.test(l));
  for (let i = 0; i < lines.length; i++) {
    const zeile = lines[i] ?? "";
    // Pre.786 (#52): Tabellen-Runs IM Block erkennen — Prosa, die per Single-Newline an der Tabelle klebt, bleibt Absatz.
    if (PIPE_ROW_RE.test(zeile) && PIPE_SEP_RE.test(lines[i + 1] ?? "")) {
      flushPara();
      let j = i;
      while (j < lines.length && PIPE_ROW_RE.test(lines[j] ?? "")) j++;
      renderTableRun(target, lines.slice(i, j));
      i = j - 1;
      continue;
    }
    const abschnitt = ABSCHNITT_RE.exec(zeile);
    if (abschnitt) {
      flushPara();
      const p = document.createElement("p");
      p.className = "md-abschnitt";
      const kopf = document.createElement("strong");
      kopf.className = "md-abschnitt-kopf";
      kopf.textContent = `${abschnitt[1] ?? ""}:`;
      p.appendChild(kopf);
      const rest = zeile.slice(abschnitt[0].length);
      if (rest.trim()) {
        p.appendChild(document.createTextNode(" "));
        renderInline(p, rest);
      }
      target.appendChild(p);
      continue;
    }
    const art = PUNKT_RE.test(zeile) ? "ul" : NUMMER_RE.test(zeile) ? "ol" : null;
    const marke = art === "ul" ? PUNKT_RE : NUMMER_RE;
    // Die nächste Nummer zählt auch hinter eingerückten Folgezeilen („1. a\n   weiter\n2. b“ ist EINE Liste).
    let naechste = i + 1;
    while (naechste < lines.length && FOLGEZEILE_RE.test(lines[naechste] ?? "")) naechste++;
    if (art === "ol" && !ganzNummeriert && !NUMMER_RE.test(lines[naechste] ?? "")) {
      para.push(zeile); // eine einzelne „20. Oktober …“-Zeile ist kein Listenpunkt
      continue;
    }
    if (!art) {
      para.push(zeile);
      continue;
    }
    flushPara();
    const punkte: string[] = [];
    let j = i;
    while (j < lines.length) {
      const l = lines[j] ?? "";
      if (marke.test(l)) punkte.push(l.replace(marke, ""));
      else if (FOLGEZEILE_RE.test(l) && punkte.length > 0) punkte[punkte.length - 1] += `\n${l.trim()}`;
      else break;
      j++;
    }
    const el = liste(art, punkte);
    if (art === "ol" && el instanceof HTMLOListElement) {
      const erste = Number(NUMMER_RE.exec(zeile)?.[1] ?? "1");
      if (erste > 1) el.start = erste;
    }
    target.appendChild(el);
    i = j - 1;
  }
  flushPara();
}

function liste(art: "ul" | "ol", punkte: string[]): HTMLElement {
  const el = document.createElement(art);
  for (const punkt of punkte) {
    const li = document.createElement("li");
    renderInline(li, punkt);
    el.appendChild(li);
  }
  return el;
}
